import React, { createContext, useState, useEffect } from "react";
import { flushSync } from "react-dom";

// Create the context
export const ThemeContext = createContext();

const THEME_KEY = "theme";

const getInitialTheme = () => {
  try {
    return localStorage.getItem(THEME_KEY) !== "light";
  } catch (e) {
    return true;
  }
};

// Colors live in App.css (:root and [data-theme="light"]), this only flips the attribute
const applyTheme = (isDark) => {
  document.documentElement.dataset.theme = isDark ? "dark" : "light";
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", isDark ? "#000000" : "#ffffff");
};

// Create a provider component
export const ThemeProvider = ({ children }) => {
  const [isDarkTheme, setIsDarkTheme] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(isDarkTheme);
    try {
      localStorage.setItem(THEME_KEY, isDarkTheme ? "dark" : "light");
    } catch (e) {}
  }, [isDarkTheme]);

  // Optional event: the new theme is revealed as a circle growing from where it was triggered.
  // Optional onSwitch runs together with the switch, so its changes show up in the new theme only.
  const toggleTheme = (event, onSwitch) => {
    const next = !isDarkTheme;
    const switchTheme = () => {
      applyTheme(next);
      flushSync(() => {
        setIsDarkTheme(next);
        if (onSwitch) onSwitch();
      });
    };

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (!document.startViewTransition || reduceMotion) {
      switchTheme();
      return;
    }

    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    if (event && event.clientX) {
      x = event.clientX;
      y = event.clientY;
    } else if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.top + rect.height / 2;
    }
    const radius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const transition = document.startViewTransition(switchTheme);
    transition.ready.then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${radius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration: 900,
          easing: "cubic-bezier(0.645, 0.045, 0.355, 1)",
          pseudoElement: "::view-transition-new(root)",
        }
      );
    });
  };

  return (
    <ThemeContext.Provider value={{ isDarkTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
